import React, {FC, ReactNode, RefObject, useMemo, type JSX} from 'react';
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView as RNScrollView,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import {Footer} from './components/footer/Footer';
import {Header as DefaultHeader} from '../../templates/header/Header';
import {Header} from './components/header/Header';
import {Panel as DefaultPanel} from '../../templates/panel/Panel';
import {Panel} from './components/panel/Panel';
import {ScrollView, ScrollViewProps} from './components/scrollView/ScrollView';
import {style} from './Layout.style';
import {useNavigationBar} from '@components/hooks/useNavigationBar';
import {Edges} from 'react-native-safe-area-context';
import {DefaultFooter} from './components/footer/DefaultFooter';
import {SafeAreaView} from '@lookiero/aurora';

interface LayoutStyle {
  readonly safeAreaView: StyleProp<ViewStyle>;
  readonly scrollView: StyleProp<ViewStyle>;
  readonly header: StyleProp<ViewStyle>;
  readonly content: StyleProp<ViewStyle>;
}

interface LayoutProps extends Partial<Omit<ScrollViewProps, 'style'>> {
  readonly children: ReactNode;
  readonly edges?: Edges;
  readonly header?: JSX.Element | null;
  readonly footer?: JSX.Element | null;
  readonly panel?: JSX.Element | null;
  readonly stickyHeader?: boolean;
  readonly stickyHeaderHiddenOnScroll?: boolean;
  readonly scrollRef?: RefObject<RNScrollView | null>;
  readonly scrollEnabled?: boolean;
  readonly scrollable?: boolean;
  readonly style?: Partial<LayoutStyle>;
  readonly onLayout?: (event: LayoutChangeEvent) => void;
  readonly onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}
const Layout: FC<LayoutProps> = ({
  children,
  edges,
  header,
  footer = <DefaultFooter />,
  panel = <DefaultPanel />,
  stickyHeader = true,
  stickyHeaderHiddenOnScroll = false,
  scrollRef,
  scrollEnabled = true,
  scrollable = true,
  style: customStyle,
  onLayout,
  onScroll,
  ...props
}) => {
  const isNavigationBarEnabled = useNavigationBar();
  const hiddenOnScroll = header === undefined ? isNavigationBarEnabled : stickyHeaderHiddenOnScroll;
  const memoizedHeader = useMemo(() => (header === undefined ? <DefaultHeader /> : header), [header]);
  const safeEdges: Edges = edges ?? (footer ? ['top', 'left', 'right'] : ['top', 'left', 'right', 'bottom']);

  const Wrapper = scrollable ? ScrollView : View;
  const wrapperProps = scrollable
    ? {
        stickyHeaderIndices: memoizedHeader && stickyHeader ? [0] : undefined,
        stickyHeaderHiddenOnScroll: hiddenOnScroll,
        onScroll,
        contentContainerStyle: [style.contentContainerStyle, customStyle?.scrollView],
        ref: scrollRef,
        scrollEnabled,
        keyboardShouldPersistTaps: 'handled' as const,
        testID: 'layout-scroll-view',
        ...props,
      }
    : {style: [style.staticContent, customStyle?.scrollView], testID: 'layout-static-view'};

  return (
    <SafeAreaView style={[style.safeAreaView, customStyle?.safeAreaView]} edges={safeEdges}>
      <Wrapper {...wrapperProps}>
        <Header sticky={stickyHeader} style={customStyle?.header}>
          {memoizedHeader}
        </Header>

        {panel && <Panel>{panel}</Panel>}

        <View style={[style.children, customStyle?.content]} onLayout={onLayout}>
          {children}
        </View>
      </Wrapper>

      {footer && <Footer>{footer}</Footer>}
    </SafeAreaView>
  );
};

export type {LayoutProps};
export {Layout};
